// src/features/atividade/estatisticas.js
//
// O que as telas de RESUMO (2B) e de DETALHE (4) desenham a partir de uma
// atividade já gravada: splits por quilômetro, o perfil de elevação, o ganho e a
// perda de altitude e a frequência cardíaca média.
//
// Módulo PURO, como o `geo.js`: nenhum React, nenhum Firestore, nenhum plugin.
// É por isso que os casos que dão errado sem fazer barulho (km cruzado no meio
// do intervalo de amostra, altitude ausente, atividade sem split gravado) têm
// teste em `estatisticas.test.js`.
//
// ─── DE ONDE VEM CADA NÚMERO, EM ORDEM DE PREFERÊNCIA ───────────────────────
//
//   splits   → SPLITS do documento (calculados no aparelho, a 1 ponto por fix)
//              → derivados das SAMPLES (uma a cada 5 s: tempo, altitude, pace)
//   elevação → ELEVACAO_POS/NEG do documento → altitude das SAMPLES
//   perfil   → pontos crus da gravação (Resumo) → altitude das SAMPLES (Detalhe)
//
// O documento salvo NÃO tem os pontos crus: a polyline guarda só latitude e
// longitude (ver polyline.js e atividadeService.salvarAtividade). Altitude e
// tempo vivem nas SAMPLES. Por isso o perfil do Detalhe tem o TEMPO no eixo x
// e o do Resumo, que ainda tem os pontos na memória, tem a DISTÂNCIA.

import { elevacao, finito, haversine, suavizarAltitude } from './geo.js';

/** Sobra menor que isto não vira split parcial — a mesma régua do geo.js. */
const SOBRA_MINIMA_M = 50;

/** Faixa vertical mínima do gráfico de elevação, em metros. */
const FAIXA_MINIMA_M = 10;

/** Pontos no máximo no desenho do perfil. Mais que isso não muda um pixel. */
const MAX_PONTOS_PERFIL = 120;

const num = (v) => (finito(v) ? Number(v) : null);

// ── Splits ───────────────────────────────────────────────────────────────────

/**
 * Corta uma série de marcos `{d, t, alt}` (d = metros acumulados, t =
 * segundos) em quilômetros.
 *
 * O instante exato do km cai no meio de um intervalo quase sempre, então tempo
 * e altitude são interpolados dentro do intervalo que cruza a marca — sem isso,
 * com uma amostra a cada 5 s, o erro de um km passaria inteiro para o seguinte.
 *
 * @param {Array<{d:number,t:number,alt:(number|null)}>} marcos em ordem
 * @returns {Array<{km:number,paceS:number,eleM:number,distanciaM:number,parcial:boolean}>}
 */
export function cortarEmKm(marcos) {
  if (!Array.isArray(marcos) || marcos.length < 2) return [];

  const splits = [];
  let marcaD = 0;
  let marcaT = marcos[0].t;
  let marcaAlt = num(marcos[0].alt);
  const EPS = 1e-6;

  for (let i = 1; i < marcos.length; i += 1) {
    const a = marcos[i - 1];
    const b = marcos[i];
    const trecho = b.d - a.d;
    if (!(trecho > 0)) continue;

    const altA = num(a.alt);
    const altB = num(b.alt);

    while (Math.floor((b.d + EPS) / 1000) > Math.floor((marcaD + EPS) / 1000)) {
      const alvo = (Math.floor((marcaD + EPS) / 1000) + 1) * 1000;
      const fracao = Math.min(1, Math.max(0, (alvo - a.d) / trecho));
      const tAlvo = a.t + (b.t - a.t) * fracao;
      const altAlvo =
        altA != null && altB != null ? altA + (altB - altA) * fracao : altB ?? altA;

      splits.push({
        km: Math.round(alvo / 1000),
        paceS: Math.round(tAlvo - marcaT),
        eleM: marcaAlt != null && altAlvo != null ? Math.round(altAlvo - marcaAlt) : 0,
        distanciaM: 1000,
        parcial: false,
      });

      marcaD = alvo;
      marcaT = tAlvo;
      marcaAlt = altAlvo;
    }
  }

  const ultimo = marcos[marcos.length - 1];
  const sobra = ultimo.d - marcaD;
  if (sobra >= SOBRA_MINIMA_M) {
    const altFim = num(ultimo.alt);
    splits.push({
      km: splits.length + 1,
      // Normalizado por km, como no geo.js: a sobra de 300 m não pode
      // aparecer como "o km mais rápido da vida" só por ser curta.
      paceS: Math.round(((ultimo.t - marcaT) / sobra) * 1000),
      eleM: marcaAlt != null && altFim != null ? Math.round(altFim - marcaAlt) : 0,
      distanciaM: Math.round(sobra),
      parcial: true,
    });
  }

  return splits;
}

/**
 * Splits a partir das SAMPLES do documento — a rede para quando o SPLITS não
 * foi gravado (atividade recuperada de um buffer antigo, documento de antes do
 * campo existir).
 *
 * As amostras não têm distância: têm o pace móvel dos últimos 200 m. A
 * distância de cada intervalo sai de `dt / pace`, e o total ESTIMADO é então
 * esticado até a distância de verdade do documento, que é a do traçado — o que
 * a integração erra fica distribuído proporcionalmente, em vez de acumulado no
 * último km.
 *
 * @param {Array<{t:number,alt:(number|null),paceS:(number|null)}>} amostras
 * @param {number} distanciaM distância total medida
 * @returns {Array} splits no mesmo formato de `cortarEmKm`
 */
export function splitsDeAmostras(amostras, distanciaM) {
  const lista = (Array.isArray(amostras) ? amostras : [])
    .filter((a) => finito(a?.t))
    .map((a) => ({ t: Number(a.t), alt: num(a.alt), paceS: num(a.paceS) }))
    .sort((a, b) => a.t - b.t);
  if (lista.length < 2) return [];

  const velocidade = (a) => (a.paceS > 0 ? 1000 / a.paceS : null);

  let acumulado = 0;
  const marcos = [{ d: 0, t: lista[0].t, alt: lista[0].alt }];
  for (let i = 1; i < lista.length; i += 1) {
    const a = lista[i - 1];
    const b = lista[i];
    const dt = b.t - a.t;
    const va = velocidade(a);
    const vb = velocidade(b);
    // Média das duas pontas quando existem; uma só quando falta a outra; zero
    // quando nenhuma diz nada (começo da atividade, antes da janela do pace).
    const v = va != null && vb != null ? (va + vb) / 2 : vb ?? va ?? 0;
    acumulado += dt > 0 ? v * dt : 0;
    marcos.push({ d: acumulado, t: b.t, alt: b.alt });
  }
  if (!(acumulado > 0)) return [];

  const real = Number(distanciaM);
  const escala = real > 0 ? real / acumulado : 1;
  return cortarEmKm(marcos.map((m) => ({ ...m, d: m.d * escala })));
}

/**
 * Os splits que a tela mostra: os do documento quando existem, os derivados
 * das amostras quando não.
 *
 * @param {object} args `{ splits, amostras, distanciaM }`
 * @returns {Array}
 */
export function splitsDaAtividade({ splits, amostras, distanciaM } = {}) {
  const gravados = (Array.isArray(splits) ? splits : []).filter(
    (s) => Number(s?.paceS) > 0
  );
  if (gravados.length) return gravados;
  return splitsDeAmostras(amostras, distanciaM);
}

/**
 * Comprimento da barra de cada split e qual é o km mais rápido (handoff §4).
 *
 * A barra é o pace do km comparado ao MAIS RÁPIDO da atividade, invertido: o
 * km mais rápido enche o trilho. Comparar com o pace médio deixaria metade das
 * barras maiores que o trilho.
 *
 * A referência é o mais rápido entre os km COMPLETOS. O parcial do fim é
 * projetado por km e com 60 m de amostra vira qualquer número — se ele virasse
 * a régua, todas as outras barras encolheriam por causa de um pedaço de rua.
 *
 * @param {Array} splits
 * @returns {Array} cada split com `largura` (8–100, em %) e `maisRapido`
 */
export function barrasDeSplits(splits) {
  const lista = Array.isArray(splits) ? splits : [];
  const validos = lista.filter((s) => Number(s?.paceS) > 0);
  const completos = validos.filter((s) => !s.parcial);
  const base = completos.length ? completos : validos;
  const referencia = base.length ? Math.min(...base.map((s) => Number(s.paceS))) : 0;

  // Destacar "o mais rápido" só tem sentido com pelo menos dois para comparar.
  let marcado = completos.length < 2;

  return lista.map((s) => {
    const pace = Number(s?.paceS) || 0;
    const largura =
      referencia > 0 && pace > 0
        ? Math.max(8, Math.min(100, Math.round((referencia / pace) * 100)))
        : 8;
    const maisRapido = !marcado && !s.parcial && pace === referencia;
    if (maisRapido) marcado = true;
    return { ...s, largura, maisRapido };
  });
}

// ── Elevação ─────────────────────────────────────────────────────────────────

/**
 * Média por baldes até caber em `max` pontos. Média e não "um a cada N": pegar
 * um ponto solto de cada balde desenharia o pico ou o vale que caísse ali, e o
 * mesmo trajeto sairia com cara diferente dependendo de onde o corte caiu.
 */
function reduzir(serie, max) {
  if (serie.length <= max) return serie;
  const saida = [];
  for (let k = 0; k < max; k += 1) {
    const ini = Math.floor((k * serie.length) / max);
    const fim = Math.max(ini + 1, Math.floor(((k + 1) * serie.length) / max));
    let sx = 0;
    let sa = 0;
    for (let i = ini; i < fim; i += 1) {
      sx += serie[i].x;
      sa += serie[i].alt;
    }
    const n = fim - ini;
    saida.push({ x: sx / n, alt: sa / n });
  }
  // As pontas ficam nas pontas: a média do primeiro balde não começa em x = 0,
  // e o gráfico nasceria descolado da borda esquerda.
  saida[0] = { ...saida[0], x: 0 };
  saida[saida.length - 1] = { ...saida[saida.length - 1], x: 1 };
  return saida;
}

/**
 * Perfil de elevação normalizado: `x` de 0 a 1, `alt` em metros, suavizado.
 *
 * Com `pontos` (a gravação crua, que só existe no Resumo) o eixo x é a
 * DISTÂNCIA percorrida. Com `amostras` (o que o documento guarda) é o TEMPO em
 * movimento.
 *
 * A altitude passa pela mesma média móvel do cálculo de desnível
 * (`suavizarAltitude`): sem ela o gráfico mostra o erro vertical do GPS, que
 * oscila metros com o aparelho parado, como se fosse ladeira.
 *
 * @param {object} args `{ pontos, amostras, maxPontos }`
 * @returns {{serie:Array<{x:number,alt:number}>,min:number,max:number,eixo:string}|null}
 */
export function perfilDeElevacao({ pontos, amostras, maxPontos = MAX_PONTOS_PERFIL } = {}) {
  let brutos = [];
  let eixo = 'tempo';

  if (Array.isArray(pontos) && pontos.length >= 2) {
    eixo = 'distancia';
    let d = 0;
    brutos = pontos.map((p, i) => {
      if (i > 0) d += haversine(pontos[i - 1], p);
      return { pos: d, alt: num(p?.alt) };
    });
  } else if (Array.isArray(amostras) && amostras.length >= 2) {
    brutos = amostras
      .filter((a) => finito(a?.t))
      .map((a) => ({ pos: Number(a.t), alt: num(a.alt) }))
      .sort((a, b) => a.pos - b.pos);
  }

  const suave = suavizarAltitude(brutos.map((b) => b.alt));
  const validos = brutos
    .map((b, i) => ({ pos: b.pos, alt: suave[i] }))
    .filter((b) => finito(b.alt));
  if (validos.length < 2) return null;

  const ini = validos[0].pos;
  const extensao = validos[validos.length - 1].pos - ini;
  const serie = validos.map((v, i) => ({
    // Sem extensão (trajeto parado, carimbos iguais), o índice é o eixo.
    x: extensao > 0 ? (v.pos - ini) / extensao : i / (validos.length - 1),
    alt: v.alt,
  }));

  const reduzida = reduzir(serie, Math.max(2, maxPontos));
  const alts = reduzida.map((p) => p.alt);
  return { serie: reduzida, min: Math.min(...alts), max: Math.max(...alts), eixo };
}

/**
 * Os dois `d` de SVG do gráfico: a linha e a área sob ela.
 *
 * A faixa vertical tem um MÍNIMO de 10 m, centrado no trajeto. Sem isso, uma
 * volta no parque plano (2 m de variação) ocuparia a altura inteira do gráfico
 * e pareceria uma serra — o gráfico tem de dizer "plano" quando é plano.
 *
 * @param {object} perfil saída de `perfilDeElevacao`
 * @param {object} caixa `{ largura, altura, respiro }` em unidades do viewBox
 * @returns {{linha:string, area:string, y:Function}|null}
 */
export function desenhoDoPerfil(perfil, { largura = 358, altura = 64, respiro = 4 } = {}) {
  if (!perfil?.serie?.length || perfil.serie.length < 2) return null;
  const amplitude = perfil.max - perfil.min;
  const faixa = Math.max(FAIXA_MINIMA_M, amplitude);
  const base = perfil.min - (faixa - amplitude) / 2;
  const util = altura - respiro * 2;

  const y = (alt) => respiro + (1 - (alt - base) / faixa) * util;
  const linha = perfil.serie
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(p.x * largura).toFixed(1)} ${y(p.alt).toFixed(1)}`)
    .join(' ');

  return { linha, area: `${linha} L${largura} ${altura} L0 ${altura}Z`, y };
}

/**
 * Altitude do perfil numa posição `fracao` (0 a 1), interpolada — o que o
 * marcador do gráfico mostra quando o dedo passa por ele.
 *
 * @param {object} perfil
 * @param {number} fracao
 * @returns {number|null}
 */
export function altitudeEm(perfil, fracao) {
  const serie = perfil?.serie;
  if (!Array.isArray(serie) || !serie.length || !finito(fracao)) return null;
  const f = Math.max(0, Math.min(1, Number(fracao)));
  for (let i = 1; i < serie.length; i += 1) {
    const a = serie[i - 1];
    const b = serie[i];
    if (f <= b.x) {
      const vao = b.x - a.x;
      return vao > 0 ? a.alt + ((f - a.x) / vao) * (b.alt - a.alt) : b.alt;
    }
  }
  return serie[serie.length - 1].alt;
}

/**
 * Ganho e perda de altitude da atividade.
 *
 * O número gravado no documento ganha sempre que EXISTE, inclusive zero: ele
 * foi calculado sobre os pontos a 1 Hz, e recalcular sobre amostras a cada 5 s
 * daria outro número para a mesma corrida. O cálculo só entra quando o campo
 * falta (documento antigo).
 *
 * @param {object} args `{ elevacaoPos, elevacaoNeg, amostras, pontos }`
 * @returns {{pos:number, neg:number}}
 */
export function elevacaoDaAtividade({ elevacaoPos, elevacaoNeg, amostras, pontos } = {}) {
  if (finito(elevacaoPos) || finito(elevacaoNeg)) {
    return {
      pos: Math.max(0, Math.round(Number(elevacaoPos) || 0)),
      neg: Math.max(0, Math.round(Number(elevacaoNeg) || 0)),
    };
  }
  if (Array.isArray(pontos) && pontos.length >= 2) return elevacao(pontos);
  const serie = (Array.isArray(amostras) ? amostras : []).map((a) => ({ alt: a?.alt }));
  return elevacao(serie);
}

// ── Frequência cardíaca ──────────────────────────────────────────────────────

const fcPlausivel = (v) => finito(v) && Number(v) >= 30 && Number(v) <= 240;

/**
 * Frequência cardíaca média, em bpm, ou `null` quando não há dado.
 *
 * HOJE NENHUMA GRAVAÇÃO TEM ESTE DADO. O GPS não mede batimento; quem mede é
 * um relógio ou uma cinta, e o app não lê nenhum dos dois durante a gravação
 * (o plugin de HealthKit só consulta repouso e variabilidade, e no Android não
 * há Health Connect). A função existe para o dia em que o documento trouxer
 * `FC_MEDIA` — ou amostras com `fc` — e a tela não precisar mudar.
 *
 * Valor fora de 30–240 bpm é tratado como ausente: é leitura de sensor solto,
 * não coração.
 *
 * @param {object} atividade documento de ATIVIDADES
 * @returns {number|null}
 */
export function fcMediaDaAtividade(atividade) {
  const direta = atividade?.FC_MEDIA ?? atividade?.fcMedia;
  if (fcPlausivel(direta)) return Math.round(Number(direta));

  const leituras = (Array.isArray(atividade?.SAMPLES) ? atividade.SAMPLES : [])
    .map((a) => a?.fc ?? a?.hr)
    .filter(fcPlausivel)
    .map(Number);
  if (!leituras.length) return null;
  return Math.round(leituras.reduce((s, v) => s + v, 0) / leituras.length);
}

// ── Ponte com o check-in ─────────────────────────────────────────────────────

/**
 * Este registro de treino é o espelho de uma atividade gravada por GPS?
 *
 * Toda atividade de GPS cria um check-in (é por ele que ela ganha XP), com
 * `atividades[].gps = true` montado em `montarAtividadeDoCheckin`. Aqui a flag
 * é só para DECIDIR QUAL TELA ABRIR — não decide XP nem pontos, e por isso
 * pode confiar no que o próprio app gravou.
 *
 * @param {object} checkin item de `listTreinoCheckins`
 * @returns {boolean}
 */
export function ehCheckinDeGps(checkin) {
  if (!checkin || checkin.recovery === true) return false;
  if (checkin.ATIVIDADE_GPS_ID || checkin.atividade_gps_id) return true;
  return (Array.isArray(checkin.atividades) ? checkin.atividades : []).some(
    (a) => a?.gps === true
  );
}

/**
 * Índice do traçado que corresponde a uma posição relativa do gráfico.
 *
 * O traçado e as amostras têm densidades diferentes (um ponto por fix, uma
 * amostra a cada 5 s). A ponte é a POSIÇÃO RELATIVA, não o índice.
 *
 * @param {number} fracao 0 a 1
 * @param {number} total quantidade de pontos do traçado
 * @returns {number|null}
 */
export function indiceNoTracado(fracao, total) {
  if (!(total > 0) || !finito(fracao)) return null;
  const f = Math.max(0, Math.min(1, Number(fracao)));
  return Math.round(f * (total - 1));
}

const estatisticas = {
  cortarEmKm,
  splitsDeAmostras,
  splitsDaAtividade,
  barrasDeSplits,
  perfilDeElevacao,
  desenhoDoPerfil,
  altitudeEm,
  elevacaoDaAtividade,
  fcMediaDaAtividade,
  ehCheckinDeGps,
  indiceNoTracado,
};

export default estatisticas;
